import React, { useState, useEffect, useMemo, useRef } from "react";
import { fireGoogleAdsConversion } from '../../utils/googleAdsTracking';
import pixIcon from '../../static/images/pix_icon.png';
import { styled as styledMUI } from "@mui/material/styles";
import {
  Modal,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import styled, { useTheme } from "styled-components";
import { toast } from 'react-toastify';
import InputMask from "react-input-mask";
import { API_BASE, TOAST_CONFIG } from "../../shared/Constants";
import { useAuth0 } from "@auth0/auth0-react";
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { QRCodeSVG } from 'qrcode.react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

let stripePromise: Promise<Stripe | null> | null = null;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = async <T,>(promise: Promise<T>, ms: number, label = "timeout"): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

const loadStripeResilient = async (key: string): Promise<Stripe | null> => {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= 5; attempt += 1) {
    try {
      const stripe = await withTimeout(loadStripe(key), 12000, "loadStripe timeout");
      if (stripe) {
        return stripe;
      }
      lastError = new Error("loadStripe returned null");
    } catch (error) {
      lastError = error;
    }

    if (attempt < 5) {
      const baseDelay = Math.min(5000, 500 * 2 ** (attempt - 1));
      const jitter = Math.floor(Math.random() * 250);
      await sleep(baseDelay + jitter);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const getStripe = (): Promise<Stripe | null> => {
  const key = process.env.REACT_APP_STRIPE_KEY;
  if (!key) {
    return Promise.reject(new Error("Missing REACT_APP_STRIPE_KEY"));
  }

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return Promise.reject(new Error("Offline"));
  }

  if (!stripePromise) {
    stripePromise = loadStripeResilient(key).catch((error) => {
      stripePromise = null;
      throw error;
    });
  }

  return stripePromise;
};

export interface SubscriptionModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubscriptionChange?: () => void;
}


export default function SubscriptionModal({
  openModal,
  setOpenModal,
  onSubscriptionChange,
}: SubscriptionModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isDarkStripeTheme = theme?.mode === 'dark';

  // Local state for plans and locations
  type PlanData = {
    id: string;
    title: string;
    price: number;
    period: string;
    tagline: string;
    features: string[];
    is_current: boolean;
    is_canceled?: boolean;
    subscription_expires_at?: number;
  };

  type Locations = {
    [state: string]: {
      [city: string]: string;
    };
  };

  const [plans, setPlans] = useState<Array<PlanData & { cta: string }>>([]);
  const [locations, setLocations] = useState<Locations>({});
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const generatePlanCTA = (plan: PlanData, allPlans: PlanData[]): string => {
    if (plan.is_current) {
      if (plan.id === "premium") {
        if (plan.is_canceled) {
          return "Reativar assinatura";
        } else {
          return "Alterar dados fiscais";
        }
      } else {
        return "Plano atual";
      }
    } else {
      // Verify if user has an active premium subscription
      const hasPremiumActive = allPlans.some(p => p.id === "premium" && p.is_current && !p.is_canceled);
      // Also check if there's a canceled premium subscription
      const hasPremiumCanceled = allPlans.some(p => p.id === "premium" && p.is_current && p.is_canceled);
      
      if ((hasPremiumActive || hasPremiumCanceled) && plan.id !== "premium") {
        return "Voltar ao plano gratuito";
      } else {
        return "Assinar";
      }
    }
  };

  const refreshPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await fetch(`${API_BASE}/api/subscription-plans`, {
        headers: { Authorization: `Auth ${await getAccessTokenSilently()}` },
      });
      if (response.ok) {
        const data: PlanData[] = await response.json();
        setPlans(data.map((p) => ({ ...p, cta: generatePlanCTA(p, data) })));
      } else {
        setPlans([]);
      }
    } catch (err) {
      toast.error("Erro ao carregar planos de assinatura", TOAST_CONFIG);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const refreshLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await fetch(`${API_BASE}/api/states-cities`, {
        headers: { Authorization: `Auth ${await getAccessTokenSilently()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (err) {
      toast.error("Erro ao carregar lista de estados e cidades", TOAST_CONFIG);
    } finally {
      setLoadingLocations(false);
    }
  };

  const isInitialLoading = loadingPlans && plans.length === 0;
  const isRefreshing = loadingPlans && plans.length > 0;
  // When the modal just opened, force showing skeleton even if we have stale data
  const prevOpenRef = useRef(openModal);
  const openedNow = openModal && !prevOpenRef.current;
  useEffect(() => {
    prevOpenRef.current = openModal;
  }, [openModal]);
  const showPlansSkeleton = openedNow || isInitialLoading;

  // Remove old state management as it's now handled by context
  const [editingInvoice, setEditingInvoice] = useState(false);

  const brazilianStates = useMemo(
    () =>
      Object.keys(locations).map((uf) => ({
        id: uf,
        label: uf,
      })),
    [locations]
  );

  const getCitiesByState = useMemo(
    () =>
      (uf: string) =>
        uf && locations[uf]
          ? Object.entries(locations[uf]).map(([id, label]) => ({ id, label }))
          : [],
    [locations]
  );

  const [cancelLoading, setCancelLoading] = useState<string | null>(null); // plan id
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [ctaLoading, setCtaLoading] = useState<string | null>(null); // plan id for CTA button loading
  const [portalLoadingPlanId, setPortalLoadingPlanId] = useState<string | null>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [planToCancel, setPlanToCancel] = useState<PlanData | null>(null);
  const formatSubscriptionExpiration = (timestamp?: number | null) => {
    if (!timestamp) {
      return null;
    }
    const date = new Date(timestamp * 1000);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };
  
  const cancelSubscription = async (planId: string) => {
    const loadingKey = planId === "premium" ? `cancel-${planId}` : planId;
    setCancelLoading(loadingKey);
    try {
      const response = await fetch(`${API_BASE}/api/billing/cancel_subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${await getAccessTokenSilently()}`,
        },
      });
      if (!response.ok) {
        toast.error("Erro ao cancelar assinatura", TOAST_CONFIG);
      } else {
        toast.success("Assinatura cancelada com sucesso!", TOAST_CONFIG);
        // Refresh plans data to reflect changes using context
        await refreshPlans();
        onSubscriptionChange?.();
      }
      closeModal();
    } catch (error) {
      toast.error("Erro ao cancelar assinatura", TOAST_CONFIG);
    } finally {
      setCancelLoading(null);
    }
  };

  const reactivateSubscription = async () => {
    setReactivateLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/billing/checkout?from_chat=true`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${await getAccessTokenSilently()}`,
        },
      });

      if (response.ok) {
        const checkoutData = await response.json();
        if (checkoutData.status === "subscription_reactivated") {
          toast.success("Assinatura reativada com sucesso!", TOAST_CONFIG);
          // Refresh plans data to reflect changes using context
          await refreshPlans();
          onSubscriptionChange?.();
          closeModal();
        } else if (checkoutData.client_secret) {
          // Use Elements with received client secret
          setClientSecret(checkoutData.client_secret);
          setIntentType(checkoutData.type || "payment");
          setCheckoutKey(prev => prev + 1); // Force new checkout instance
          setStep("checkout");
        } else if (checkoutData.url) {
          setCheckoutKey(prev => prev + 1);
          setStep("checkout");
        }
      } else if (response.status === 409) {
        toast.error("Você já possui uma assinatura ativa. Por favor contate o suporte em caso de problemas.", TOAST_CONFIG);
      } else {
        toast.error("Erro ao reativar assinatura", TOAST_CONFIG);
      }
    } catch (error) {
      toast.error("Erro ao reativar assinatura", TOAST_CONFIG);
    } finally {
      setReactivateLoading(false);
    }
  };

  const openCustomerPortal = async (planId: string) => {
    setPortalLoadingPlanId(planId);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/billing/customer-portal`, {
        headers: {
          Authorization: `Auth ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer portal URL: ${response.status}`);
      }

      const data = await response.json();
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Missing customer portal URL');
      }
    } catch (error) {
      console.error('Erro ao abrir portal do cliente', error);
      toast.error('Erro ao abrir portal de pagamento. Tente novamente mais tarde.', TOAST_CONFIG);
    } finally {
      setPortalLoadingPlanId(null);
    }
  };

  const [step, setStep] = useState<"plans" | "invoice-config" | "payment-methods" | "checkout" | "pix-subscription">("plans");
  const [pixApproved, setPixApproved] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentType, setIntentType] = useState<string | null>(null);
  const [checkoutKey, setCheckoutKey] = useState<number>(0); // Add key to force remount

  // Function to reset all modal state
  const resetModalState = () => {
    setStep("plans");
    setCompleted(false);
    setClientSecret(null);
    setEditingInvoice(false);
    setCancelLoading(null);
    setReactivateLoading(false);
    setCtaLoading(null);
    setPortalLoadingPlanId(null);
    setShowCancelConfirmation(false);
    setPlanToCancel(null);
    setCheckoutKey(prev => prev + 1); // Force remount of checkout component
  };

  // Function to close modal and reset state
  const closeModal = () => {
    setOpenModal(false);
    resetModalState();
  };

  useEffect(() => {
    if (!openModal) return;
    // Fetch once when modal opens
    Promise.allSettled([
      refreshPlans(),
      refreshLocations(),
    ]);
  }, [openModal]); // Removed function dependencies to prevent infinite loop
  
  const PlansContainer = () => (
    <ModalContainer step="plans">
      <Header>
        <h1 id="subscription-modal-title">Escolha seu plano</h1>
        <HeaderActions>
          {isRefreshing && (
            <RefreshIndicator>
              <CircularProgress size={16} />
            </RefreshIndicator>
          )}
          <CloseButton
            onClick={closeModal}
            aria-label="Fechar modal"
          >
            <CloseIcon />
          </CloseButton>
        </HeaderActions>
      </Header>

      <ScrollableContent step="plans">
        {showPlansSkeleton ? (
          <CardsRow>
            {[1, 2].map((i) => (
              <PlanCard key={i}>
                <PriceBlock>
                  <Skeleton variant="text" width={120} height={28} style={{ marginBottom: 8 }} />
                  <Price>
                    <Skeleton variant="text" width={80} height={40} style={{ display: 'inline-block', marginRight: 8 }} />
                    <Unit><Skeleton variant="text" width={40} height={18} style={{ display: 'inline-block' }} /></Unit>
                  </Price>
                  <Skeleton variant="text" width={100} height={18} style={{ marginTop: 8 }} />
                </PriceBlock>
                <CTA variant="outlined" disabled fullWidth>
                  <Skeleton variant="rectangular" width="80%" height={26} />
                </CTA>
                <FeatureList>
                  {[1, 2, 3].map((j) => (
                    <li key={j}>
                      <Skeleton variant="text" width={80} height={16} />
                    </li>
                  ))}
                </FeatureList>
                <HelpLink>
                  <Skeleton variant="text" width={80} height={12} />
                </HelpLink>
              </PlanCard>
            ))}
          </CardsRow>
        ) : (
          <CardsRow>
            {plans.map((plan) => {
              const formattedExpiration = formatSubscriptionExpiration(plan.subscription_expires_at);
              const renderPlanCTA = () => (
                <CTA
                  variant={plan.is_current ? "outlined" : "contained"}
                  disabled={
                    (plan.is_current && plan.id !== "premium") || 
                    ctaLoading === plan.id ||
                    // Desabilitar botão "Voltar ao plano gratuito" quando há cancelamento programado
                    (!plan.is_current && plan.id !== "premium" && plans.some(p => p.id === "premium" && p.is_current && p.is_canceled))
                  }
                  fullWidth
                  onClick={async () => {
                    // Prevent multiple clicks
                    if (ctaLoading === plan.id) return;
                    
                    // Verificar se o botão está desabilitado devido ao cancelamento programado
                    const hasPremiumCanceled = plans.some(p => p.id === "premium" && p.is_current && p.is_canceled);
                    if (!plan.is_current && plan.id !== "premium" && hasPremiumCanceled) {
                      return; // Não fazer nada se o botão estiver desabilitado por cancelamento programado
                    }
                    
                    setCtaLoading(plan.id);
                    try {
                      if (plan.id === "premium") {
                        if (plan.is_canceled) {
                          // Reactivate canceled subscription
                          await reactivateSubscription();
                        } else {
                          // Edit fiscal data
                          setStep("invoice-config");
                          setEditingInvoice(plan.is_current);
                        }
                      } else if (!plan.is_current) {
                        // Check if user has an active premium subscription
                        const hasPremiumActive = plans.some(p => p.id === "premium" && p.is_current && !p.is_canceled);
                        
                        if (hasPremiumActive && plan.id !== "premium") {
                          // Downgrade to free plan - show confirmation dialog
                          const premiumPlan = plans.find(p => p.id === "premium" && p.is_current && !p.is_canceled);
                          if (premiumPlan) {
                            setPlanToCancel(premiumPlan);
                            setShowCancelConfirmation(true);
                          }
                        } else if (!hasPremiumActive && !hasPremiumCanceled) {
                          // Start subscription process for non-premium plans (only if no premium subscription exists)
                          setStep("invoice-config");
                          setEditingInvoice(false);
                        }
                      }
                    } finally {
                      setCtaLoading(null);
                    }
                  }}
                  startIcon={(plan.is_canceled && reactivateLoading) || ctaLoading === plan.id ? <CircularProgress size={18} /> : undefined}
                >
                  {ctaLoading === plan.id
                    ? "Processando..."
                    : plan.is_canceled && reactivateLoading
                      ? "Reativando..."
                      : plan.cta}
                </CTA>
              );
              return (
                <PlanCard key={plan.id}>
                  <PriceBlock>
                    <PlanNameContainer>
                      <PlanName>{plan.title}</PlanName>
                      {plan.is_current && (
                        <CurrentPlanBadge>Plano atual</CurrentPlanBadge>
                      )}
                      {plan.is_canceled && (
                        <CancelledBadge>Cancelamento programado</CancelledBadge>
                      )}
                      {/* Explicit cancel button for active, not-canceled premium plan */}
                      {plan.id === "premium" && plan.is_current && !plan.is_canceled && (
                        <CancelButton
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => {
                            setPlanToCancel(plan);
                            setShowCancelConfirmation(true);
                          }}
                          startIcon={cancelLoading === `cancel-${plan.id}` ? <CircularProgress size={14} /> : undefined}
                        >
                          {cancelLoading === `cancel-${plan.id}` ? "Cancelando..." : "Cancelar"}
                        </CancelButton>
                      )}
                    </PlanNameContainer>
                    {plan.price === 0 ? (
                      <Price>
                        R$ 0
                        <Unit>/{plan.period}</Unit>
                      </Price>
                    ) : (
                      <Price>
                        R$ <span>{plan.price.toFixed(2).replace('.', ',')}</span>
                        <Unit>/{plan.period}</Unit>
                      </Price>
                    )}
                    <Tagline>{plan.tagline}</Tagline>
                    {plan.id === "premium" && plan.is_current && plan.is_canceled && formattedExpiration && (
                      <CancellationInfo>
                        Você continuará no plano pago até {formattedExpiration}.
                      </CancellationInfo>
                    )}
                  </PriceBlock>

                  {plan.id === "premium" && plan.is_current ? (
                    <ButtonsContainer>
                      {renderPlanCTA()}
                      <SecondaryCTA
                        variant="outlined"
                        fullWidth
                        onClick={() => setStep("payment-methods")}
                      >
                        Gerenciar formas de pagamento
                      </SecondaryCTA>
                    </ButtonsContainer>
                  ) : (
                    renderPlanCTA()
                  )}

                  <FeatureList planId={plan.id}>
                    {plan.features.map((f) => (
                      <li key={f}>
                        {plan.id === "free" ? null : <CheckIcon fontSize="small" />} {f}
                      </li>
                    ))}
                  </FeatureList>

                  <HelpLink href="https://www.maritaca.ai/termos-e-condicoes" target="_blank" rel="noopener noreferrer">Termos de uso</HelpLink>
                </PlanCard>
              );
            })}
          </CardsRow>
        )}
        <p style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.7, margin: '12px 16px 0' }}>
          Ambos os planos têm limite de 100 páginas por arquivo.
        </p>
      </ScrollableContent>
    </ModalContainer>
  );

  // Remove duplicated declaration of editingInvoice/setEditingInvoice
  const InvoiceConfigContainer = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const [invoiceType, setInvoiceType] = useState<"PF" | "PJ">("PF");
    const [loadingSkipInvoice, setLoadingSkipInvoice] = useState(false);
    const [loadingSaveInvoice, setLoadingSaveInvoice] = useState(false);
    const [showForm, setShowForm] = useState(() => editingInvoice);
    const [loadingInvoiceConfig, setLoadingInvoiceConfig] = useState(false);
    const [hasPrefilledData, setHasPrefilledData] = useState(false);

    const [form, setForm] = useState({
      email: "",
      city: "",
      address: "",
      cep: "",
      state: "",
      address_number: "",
      neighborhood: "",
      address_complement: "",
      cpf: "",
      name: "",
      cnpj: "",
      razao_social: "",
    });

    const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

    useEffect(() => {
      if (editingInvoice) {
        setShowForm(true);
      }
    }, [editingInvoice]);

    useEffect(() => {
      if (!showForm || hasPrefilledData) {
        return;
      }

      let isActive = true;

      const loadInvoiceConfig = async () => {
        setLoadingInvoiceConfig(true);
        try {
          const response = await fetch(`${API_BASE}/api/billing/user-configs?from_chat=true`, {
            headers: {
              Authorization: `Auth ${await getAccessTokenSilently()}`,
              "Content-Type": "application/json",
            },
          });

          if (!isActive) {
            return;
          }

          if (response.ok) {
            const data = await response.json();
            const rawType = typeof data.invoice_type === "string" ? data.invoice_type.toUpperCase() : "";
            const normalizedType: "PF" | "PJ" = rawType === "PJ" ? "PJ" : rawType === "PF" ? "PF" : invoiceType;

            if (rawType === "PF" || rawType === "PJ") {
              setInvoiceType(normalizedType);
            }

            const config = (data.invoice_config ?? {}) as Record<string, string | undefined>;

            setForm((prev) => ({
              ...prev,
              email: user?.email || config.email || "",
              city: config.city ?? "",
              address: config.address ?? "",
              cep: config.cep ?? "",
              state: config.state ?? "",
              address_number: config.address_number ?? "",
              neighborhood: config.neighborhood ?? "",
              address_complement: config.address_complement ?? "",
              cpf: normalizedType === "PF" ? config.cpf ?? "" : "",
              name: normalizedType === "PF" ? config.name ?? "" : "",
              cnpj: normalizedType === "PJ" ? config.cnpj ?? "" : "",
              razao_social: normalizedType === "PJ" ? config.razao_social ?? "" : "",
            }));

            setErrors({});
          } else if (editingInvoice) {
            toast.error("Erro ao carregar dados fiscais atuais", TOAST_CONFIG);
          }
        } catch (error) {
          if (editingInvoice) {
            toast.error("Erro ao carregar dados fiscais atuais", TOAST_CONFIG);
          }
        } finally {
          if (isActive) {
            setHasPrefilledData(true);
            setLoadingInvoiceConfig(false);
          }
        }
      };

      loadInvoiceConfig();

      return () => {
        isActive = false;
      };
    }, [showForm, hasPrefilledData, getAccessTokenSilently, invoiceType, editingInvoice]);

    const readStatusMessage = async (response: Response, fallback: string) => {
      try {
        const contentType = response.headers.get("content-type") ?? "";
        if (contentType.includes("application/json")) {
          const data = await response.json();
          if (data && typeof data.status === "string" && data.status.trim().length > 0) {
            return data.status;
          }
          if (data && typeof data.message === "string" && data.message.trim().length > 0) {
            return data.message;
          }
        } else {
          const text = await response.text();
          if (text.trim().length > 0) {
            return text;
          }
        }
      } catch (error) {
        console.error("Erro ao ler mensagem de erro dos dados fiscais", error);
      }
      return fallback;
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cpfRegex   = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
    const cnpjRegex  = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    const cepRegex   = /^\d{5}-\d{3}$/;

    const handleChange =
      (field: keyof typeof form) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
      };

    const handleSelectChange =
      (field: keyof typeof form) =>
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
      };

    const validate = () : boolean => {
      const newErrors: typeof errors = {};

      const commonRequired: (keyof typeof form)[] = [
        "state","city","address","address_number","neighborhood","cep"
      ];

      const typeRequired: (keyof typeof form)[] =
        invoiceType === "PF"
          ? ["cpf","name"]
          : ["cnpj","razao_social"];

      [...commonRequired, ...typeRequired].forEach(k => {
        if (!form[k].trim()) newErrors[k] = "Campo obrigatório";
      });

      // Email is auto-set from account, no validation needed

      if (invoiceType === "PF" && form.cpf && !cpfRegex.test(form.cpf))
        newErrors.cpf = "CPF no formato 000.000.000-00";

      if (invoiceType === "PJ" && form.cnpj && !cnpjRegex.test(form.cnpj))
        newErrors.cnpj = "CNPJ no formato 00.000.000/0000-00";

      if (form.cep && !cepRegex.test(form.cep))
        newErrors.cep = "CEP no formato 00000-000";

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

  // New handleSubmit adapted for editing fiscal data
    const handleSubmit = async (skipInvoiceData: boolean = false) => {
      if (!skipInvoiceData && !validate()) return;

      if (skipInvoiceData) {
        setLoadingSkipInvoice(true);
      } else {
        setLoadingSaveInvoice(true);
      }

      try {
        if (skipInvoiceData) {
          // Reset fiscal data on the API when the user chooses to continue without invoices
          const response = await fetch(`${API_BASE}/api/billing/delete-invoice-config?from_chat=true`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Auth ${await getAccessTokenSilently()}`,
            },
          });
          if (!response.ok) {
            const message = await readStatusMessage(response, "Erro ao resetar dados fiscais");
            toast.error(message, TOAST_CONFIG);
            return;
          }
        } else {
          // Prepare invoice_config with only the relevant fields for the selected type
          const baseFields = {
            email: user?.email || form.email,
            city: form.city,
            address: form.address,
            cep: form.cep,
            state: form.state,
            address_number: form.address_number,
            neighborhood: form.neighborhood,
            address_complement: form.address_complement,
          };

          let invoice_config;
          if (invoiceType === "PF") {
            invoice_config = {
              ...baseFields,
              cpf: form.cpf,
              name: form.name,
            };
          } else { // PJ
            invoice_config = {
              ...baseFields,
              cnpj: form.cnpj,
              razao_social: form.razao_social,
            };
          }

          const payload = {
            invoice_type: invoiceType,
            invoice_config: invoice_config,
          };
          const response = await fetch(`${API_BASE}/api/billing/save-invoice-config?from_chat=true`,{
            method : "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization : `Auth ${await getAccessTokenSilently()}`,
            },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            const message = await readStatusMessage(response, "Erro ao salvar dados fiscais");
            toast.error(message, TOAST_CONFIG);
            return;
          }
        }

  // If we're editing fiscal data for an already active premium plan, don't go to checkout
        if (editingInvoice) {
          closeModal();
          toast.success("Dados fiscais salvos com sucesso!", TOAST_CONFIG);
          return;
        }

        // Go to payment methods step
        setStep("payment-methods");
      } finally {
        setLoadingSkipInvoice(false);
        setLoadingSaveInvoice(false);
      }
    };

  return (
    <ModalContainer step="invoice-config">
      <Header>
        <HeaderTitleWrapper>
          <StyledReturnButton onClick={() => setStep("plans")} />
          <h1 id="subscription-modal-title">Dados fiscais</h1>
        </HeaderTitleWrapper>

        <CloseButton onClick={closeModal} aria-label="Fechar modal">
          <CloseIcon />
        </CloseButton>
      </Header>

      <ScrollableContent step="invoice-config">
        {!showForm ? (
          <FiscalDataChoice>
            <h2>Como você gostaria de prosseguir?</h2>
            <p>Você pode fornecer seus dados fiscais para emissão de nota fiscal ou continuar sem eles.</p>
            
            <ChoiceButtonsContainer>
              <CTA
                variant="outlined"
                onClick={() => { handleSubmit(true); }}
                disabled={loadingSkipInvoice}
                startIcon={loadingSkipInvoice ? <CircularProgress size={18} /> : undefined}
                sx={{ flex: 1 }}
              >
                {loadingSkipInvoice ? "Processando..." : "Continuar sem dados fiscais"}
              </CTA>
              
              <CTA
                variant="contained"
                onClick={() => { setShowForm(true); }}
                sx={{ flex: 1 }}
              >
                Fornecer dados fiscais
              </CTA>
            </ChoiceButtonsContainer>
          </FiscalDataChoice>
        ) : (
          <>
            <PlatformFormDesc>Configure seus dados para emissão de notas fiscais</PlatformFormDesc>
            
            <InvoiceTypeContainer>
              <InvoiceTypeOption 
                selected={invoiceType === "PF"}
                onClick={() => setInvoiceType("PF")}
              >
                <PersonIcon />
                <InvoiceTypeText>Pessoa Física</InvoiceTypeText>
              </InvoiceTypeOption>
              <InvoiceTypeOption 
                selected={invoiceType === "PJ"}
                onClick={() => setInvoiceType("PJ")}
              >
                <BusinessIcon />
                <InvoiceTypeText>Pessoa Jurídica</InvoiceTypeText>
              </InvoiceTypeOption>
            </InvoiceTypeContainer>

            <PlatformFormGrid>
              {invoiceType === "PJ" ? (
                <>
                  <PlatformFormRow>
                    <PlatformFormLabel>CNPJ</PlatformFormLabel>
                    <InputMask mask="99.999.999/9999-99" value={form.cnpj} onChange={handleChange("cnpj")}>
                      {(props: any) => (
                        <PlatformFormInput
                          {...props}
                          placeholder="XX.XXX.XXX/XXXX-XX"
                          style={{ border: errors.cnpj ? "2px solid red" : "" }}
                        />
                      )}
                    </InputMask>
                  </PlatformFormRow>
                  
                  <PlatformFormRow>
                    <PlatformFormLabel>Razão Social</PlatformFormLabel>
                    <PlatformFormInput
                      type="text"
                      placeholder="Razão Social"
                      value={form.razao_social}
                      onChange={handleChange("razao_social")}
                      style={{ border: errors.razao_social ? "2px solid red" : "" }}
                    />
                  </PlatformFormRow>
                </>
              ) : (
                <>
                  <PlatformFormRow>
                    <PlatformFormLabel>CPF</PlatformFormLabel>
                    <InputMask mask="999.999.999-99" value={form.cpf} onChange={handleChange("cpf")}>
                      {(props: any) => (
                        <PlatformFormInput
                          {...props}
                          placeholder="XXX.XXX.XXX-XX"
                          style={{ border: errors.cpf ? "2px solid red" : "" }}
                        />
                      )}
                    </InputMask>
                  </PlatformFormRow>
                  
                  <PlatformFormRow>
                    <PlatformFormLabel>Nome</PlatformFormLabel>
                    <PlatformFormInput
                      type="text"
                      placeholder="Nome"
                      value={form.name}
                      onChange={handleChange("name")}
                      style={{ border: errors.name ? "2px solid red" : "" }}
                    />
                  </PlatformFormRow>
                </>
              )}

              <PlatformFormRow>
                <PlatformFormLabel>Email</PlatformFormLabel>
                <PlatformFormInput
                  type="email"
                  placeholder="Email"
                  value={user?.email || form.email}
                  disabled
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                />
              </PlatformFormRow>

              <PlatformFormRowSelect>
                <PlatformFormLabel>Estado</PlatformFormLabel>
                {loadingLocations ? (
                  <Skeleton variant="rectangular" width="100%" height={30} />
                ) : (
                  <PlatformFormSelect
                    value={form.state}
                    onChange={(e) => {
                      handleSelectChange("state")(e);
                      setForm((f) => ({ ...f, city: "" }));
                    }}
                    style={{ border: errors.state ? "2px solid red" : "" }}
                  >
                    <option value="">Selecione o estado</option>
                    {brazilianStates.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.label}
                      </option>
                    ))}
                  </PlatformFormSelect>
                )}
              </PlatformFormRowSelect>

              <PlatformFormRowSelect>
                <PlatformFormLabel>Cidade</PlatformFormLabel>
                {loadingLocations ? (
                  <Skeleton variant="rectangular" width="100%" height={30} />
                ) : (
                  <PlatformFormSelect
                    value={form.city}
                    onChange={handleSelectChange("city")}
                    disabled={!form.state}
                    style={{ border: errors.city ? "2px solid red" : "" }}
                  >
                    <option value="">{!form.state ? "Primeiro selecione o estado" : "Selecione a cidade"}</option>
                    {getCitiesByState(form.state).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.id}
                      </option>
                    ))}
                  </PlatformFormSelect>
                )}
              </PlatformFormRowSelect>

              <PlatformFormRow>
                <PlatformFormLabel>Endereço</PlatformFormLabel>
                <PlatformFormInput
                  type="text"
                  placeholder="Endereço"
                  value={form.address}
                  onChange={handleChange("address")}
                  style={{ border: errors.address ? "2px solid red" : "" }}
                />
              </PlatformFormRow>

              <PlatformFormRow>
                <PlatformFormLabel>Número</PlatformFormLabel>
                <PlatformFormInput
                  type="text"
                  placeholder="Número"
                  value={form.address_number}
                  onChange={handleChange("address_number")}
                  style={{ border: errors.address_number ? "2px solid red" : "" }}
                />
              </PlatformFormRow>

              <PlatformFormRow>
                <PlatformFormLabel>Bairro</PlatformFormLabel>
                <PlatformFormInput
                  type="text"
                  placeholder="Bairro"
                  value={form.neighborhood}
                  onChange={handleChange("neighborhood")}
                  style={{ border: errors.neighborhood ? "2px solid red" : "" }}
                />
              </PlatformFormRow>

              <PlatformFormRow>
                <PlatformFormLabel>Complemento</PlatformFormLabel>
                <PlatformFormInput
                  type="text"
                  placeholder="Complemento"
                  value={form.address_complement}
                  onChange={handleChange("address_complement")}
                />
              </PlatformFormRow>

              <PlatformFormRow>
                <PlatformFormLabel>CEP</PlatformFormLabel>
                <InputMask mask="99999-999" value={form.cep} onChange={handleChange("cep")}>
                  {(props: any) => (
                    <PlatformFormInput
                      {...props}
                      placeholder="XXXXX-XXX"
                      style={{ border: errors.cep ? "2px solid red" : "" }}
                    />
                  )}
                </InputMask>
              </PlatformFormRow>
            </PlatformFormGrid>

            <PlatformButtonContainer>
              <PlatformSaveButton onClick={() => { handleSubmit(); }} disabled={loadingSaveInvoice || loadingInvoiceConfig}>
                {loadingSaveInvoice ? <CircularProgress size={20} color="inherit" /> : "Salvar Dados Fiscais"}
              </PlatformSaveButton>
            </PlatformButtonContainer>
          </>
        )}
      </ScrollableContent>
    </ModalContainer>
  );
  };

  const PaymentMethodsContainer = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [cards, setCards] = useState<any[]>([]);
    const [loadingCards, setLoadingCards] = useState(true);
    const [selectedCard, setSelectedCard] = useState<string | null>(null);
    const [deletingCard, setDeletingCard] = useState<string | null>(null);
    const [addingCard, setAddingCard] = useState(false);
    const [addCardSecret, setAddCardSecret] = useState<string | null>(null);
    const [subscribing, setSubscribing] = useState(false);
    const [updatingCard, setUpdatingCard] = useState<string | null>(null);
    const hasActiveSubscription = plans.some(p => p.id === "premium" && p.is_current && !p.is_canceled);

    const handleCardClick = async (paymentId: string) => {
      setSelectedCard(paymentId);
      // If user has active subscription, update the default payment method immediately
      if (hasActiveSubscription) {
        setUpdatingCard(paymentId);
        try {
          const response = await fetch(
            `${API_BASE}/api/billing/update-subscription-payment-method?payment_id=${paymentId}`,
            {
              method: "POST",
              headers: { Authorization: `Auth ${await getAccessTokenSilently()}` },
            }
          );
          if (response.ok) {
            toast.success("Cartão da assinatura atualizado!", TOAST_CONFIG);
          } else {
            toast.error("Erro ao atualizar cartão da assinatura", TOAST_CONFIG);
          }
        } catch {
          toast.error("Erro ao atualizar cartão", TOAST_CONFIG);
        } finally {
          setUpdatingCard(null);
        }
      }
    };

    const fetchCards = async () => {
      setLoadingCards(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE}/api/billing/payment-methods`, {
          headers: { Authorization: `Auth ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const cardList = data.customer_cards || [];
          setCards(cardList);
          if (cardList.length > 0 && !selectedCard) {
            // Pre-select the Stripe Customer's default payment method
            const defaultPm = data.default_payment_method;
            if (defaultPm && cardList.some((c: any) => c.payment_id === defaultPm)) {
              setSelectedCard(defaultPm);
            } else {
              setSelectedCard(cardList[0].payment_id);
            }
          }
        }
      } catch (e) {
        console.error("Failed to fetch cards:", e);
      } finally {
        setLoadingCards(false);
      }
    };

    useEffect(() => { fetchCards(); }, []);

    const handleDeleteCard = async (paymentId: string) => {
      setDeletingCard(paymentId);
      try {
        const response = await fetch(`${API_BASE}/api/billing/delete-payment-method?payment_id=${paymentId}`, {
          method: "POST",
          headers: { Authorization: `Auth ${await getAccessTokenSilently()}` },
        });
        if (response.ok) {
          setCards(prev => prev.filter(c => c.payment_id !== paymentId));
          if (selectedCard === paymentId) setSelectedCard(null);
          toast.success("Cartão removido", TOAST_CONFIG);
        }
      } catch {
        toast.error("Erro ao remover cartão", TOAST_CONFIG);
      } finally {
        setDeletingCard(null);
      }
    };

    const handleAddCard = async () => {
      setAddingCard(true);
      try {
        const response = await fetch(`${API_BASE}/api/billing/checkout?embedded=true&save_card=true`, {
          headers: { Authorization: `Auth ${await getAccessTokenSilently()}` },
        });
        if (response.ok) {
          const data = await response.json();
          setAddCardSecret(data.client_secret);
        }
      } catch {
        toast.error("Erro ao iniciar cadastro de cartão", TOAST_CONFIG);
        setAddingCard(false);
      }
    };

    const handleSubscribeWithCard = async () => {
      if (!selectedCard) return;
      setSubscribing(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/billing/checkout?from_chat=true&payment_method_id=${selectedCard}`,
          {
            method: "GET",
            headers: {
              Authorization: `Auth ${await getAccessTokenSilently()}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.status === "subscription_created" || data.status === "subscription_reactivated") {
            try {
              fireGoogleAdsConversion({
                transaction_id: data.subscription_id || ('card_' + Date.now().toString()),
                value: 59.90,
                currency: 'BRL',
              });
            } catch { /* non-fatal */ }
            toast.success("Assinatura ativada com sucesso!", TOAST_CONFIG);
            await refreshPlans();
            onSubscriptionChange?.();
            closeModal();
          } else if (data.client_secret) {
            // Needs Elements checkout (e.g. 3D Secure)
            setClientSecret(data.client_secret);
            setIntentType(data.type || "payment");
            setCheckoutKey(prev => prev + 1);
            setStep("checkout");
          }
        } else if (response.status === 409) {
          toast.error("Você já possui uma assinatura ativa.", TOAST_CONFIG);
        } else {
          toast.error("Erro ao criar assinatura", TOAST_CONFIG);
        }
      } catch {
        toast.error("Erro ao criar assinatura", TOAST_CONFIG);
      } finally {
        setSubscribing(false);
      }
    };

    const stripe = useMemo(() => getStripe(), []);

    // Check if user already has active subscription (managing cards only)
    return (
      <ModalContainer step="payment-methods">
        <Header>
          <HeaderTitleWrapper>
            <StyledReturnButton onClick={() => setStep("plans")} />
            <h1>{hasActiveSubscription ? "Gerenciar formas de pagamento" : "Forma de pagamento"}</h1>
          </HeaderTitleWrapper>
          <CloseButton onClick={closeModal} aria-label="Fechar modal"><CloseIcon /></CloseButton>
        </Header>

        <ScrollableContent step="payment-methods">
          {addCardSecret ? (
            <div style={{ maxWidth: 480, width: '100%' }}>
              <p style={{ fontSize: '0.9rem', color: theme?.payment_modal_text_secondary, marginBottom: 16 }}>
                Cadastre um novo cartão
              </p>
              <Elements
                stripe={stripe}
                options={{
                  clientSecret: addCardSecret,
                  appearance: { theme: isDarkStripeTheme ? 'night' : 'stripe', variables: { colorPrimary: '#3b82f6', borderRadius: '8px' } },
                }}
              >
                <AddCardForm onComplete={async () => {
                  setAddCardSecret(null);
                  setAddingCard(false);
                  await fetchCards();
                  toast.success("Cartão adicionado!", TOAST_CONFIG);
                }} onError={(msg) => {
                  toast.error(msg, TOAST_CONFIG);
                }} />
              </Elements>
              <button
                onClick={() => { setAddCardSecret(null); setAddingCard(false); }}
                style={{
                  marginTop: 12, background: 'none', border: 'none', cursor: 'pointer',
                  color: theme?.payment_modal_text_secondary, fontSize: '0.85rem', textDecoration: 'underline',
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
              {loadingCards ? (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Skeleton variant="rounded" width={220} height={140} style={{ borderRadius: 12 }} />
                  <Skeleton variant="rounded" width={220} height={140} style={{ borderRadius: 12 }} />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {/* Add card as first card */}
                  <div
                    onClick={addingCard ? undefined : handleAddCard}
                    style={{
                      width: 220, height: 140, borderRadius: 12,
                      border: `2px dashed ${theme?.payment_modal_border || '#d1d5db'}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 8, cursor: addingCard ? 'not-allowed' : 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                      opacity: addingCard ? 0.5 : 1,
                    }}
                  >
                    {addingCard ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        <span style={{ fontSize: '1.5rem', color: theme?.payment_modal_text_secondary, lineHeight: 1 }}>+</span>
                        <span style={{ fontSize: '0.8rem', color: theme?.payment_modal_text_secondary, fontWeight: 500 }}>Adicionar cartão</span>
                      </>
                    )}
                  </div>
                  {cards.map(card => {
                    const isSelected = selectedCard === card.payment_id;
                    return (
                      <div
                        key={card.payment_id}
                        onClick={() => handleCardClick(card.payment_id)}
                        style={{
                          width: 220, height: 140, borderRadius: 12, cursor: 'pointer',
                          background: theme?.payment_modal_background || '#fff',
                          padding: 20, display: 'flex', flexDirection: 'column',
                          position: 'relative', overflow: 'hidden',
                          border: isSelected
                            ? `2px solid ${theme?.mode === 'dark' ? theme?.blue_color : '#3b82f6'}`
                            : `2px solid ${theme?.payment_modal_border || '#e2e8f0'}`,
                          boxShadow: isSelected
                            ? `0 4px 16px ${theme?.mode === 'dark' ? 'rgba(138,180,248,0.15)' : 'rgba(59,130,246,0.15)'}`
                            : '0 2px 8px rgba(0,0,0,0.06)',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Header: brand + delete */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
                          <span style={{
                            fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            background: theme?.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f8fafc',
                            border: `1px solid ${theme?.payment_modal_border || '#e2e8f0'}`,
                            borderRadius: 6, padding: '4px 8px',
                            color: theme?.payment_modal_text_secondary,
                          }}>
                            {card.card_brand}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.payment_id); }}
                            disabled={deletingCard === card.payment_id}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#ef4444'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
                            style={{
                              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                              borderRadius: '50%', width: 22, height: 22,
                              color: '#ef4444', fontSize: '0.85rem', fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s', padding: 0, lineHeight: 1,
                              opacity: deletingCard === card.payment_id ? 0.3 : 1,
                            }}
                            title="Remover cartão"
                          >
                            ×
                          </button>
                        </div>
                        {/* Body: number + info */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, justifyContent: 'center' }}>
                          <div style={{
                            fontSize: '1rem', fontWeight: 600, letterSpacing: '2px',
                            fontFamily: "'Monaco', 'Menlo', 'Courier New', monospace",
                            color: theme?.payment_modal_text,
                          }}>
                            •••• •••• •••• {card.card_last4}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: theme?.payment_modal_text_secondary, fontWeight: 500, textTransform: 'uppercase' }}>
                              Exp: {card.card_expiration}
                            </span>
                            {card.billing_name && (
                              <span style={{
                                fontSize: '0.75rem', color: theme?.payment_modal_text_secondary, fontWeight: 500,
                                textTransform: 'uppercase', maxWidth: 100, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {card.billing_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Selected indicator or loading */}
                        {updatingCard === card.payment_id ? (
                          <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.1)', borderRadius: 12,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <CircularProgress size={20} />
                          </div>
                        ) : isSelected && (
                          <div style={{
                            position: 'absolute', top: -1, right: -1,
                            width: 20, height: 20,
                            background: '#10b981', borderRadius: '0 12px 0 12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}


                </div>
              )}

              {!hasActiveSubscription && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                  <button
                    onClick={handleSubscribeWithCard}
                    disabled={!selectedCard || subscribing}
                    style={{
                      padding: '14px 24px', borderRadius: 8,
                      background: !selectedCard || subscribing ? (theme?.payment_modal_border || '#ccc') : (theme?.mode === 'dark' ? theme?.blue_color : '#4f46e5'),
                      color: !selectedCard || subscribing ? (theme?.payment_modal_text_secondary) : '#fff',
                      border: 'none', fontSize: '0.95rem', fontWeight: 600, cursor: !selectedCard || subscribing ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    {subscribing ? <><CircularProgress size={18} color="inherit" /> Processando...</> : "Confirmar assinatura com cartão"}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 1, background: theme?.payment_modal_border || '#e5e7eb' }} />
                    <span style={{ fontSize: '0.8rem', color: theme?.payment_modal_text_secondary, fontWeight: 500 }}>ou</span>
                    <div style={{ flex: 1, height: 1, background: theme?.payment_modal_border || '#e5e7eb' }} />
                  </div>
                  <button
                    onClick={() => setStep("pix-subscription")}
                    style={{
                      padding: '14px 24px', borderRadius: 8,
                      background: 'linear-gradient(135deg, #32BCAD 0%, #2A9D8F 100%)',
                      color: '#fff',
                      border: 'none', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 2px 4px rgba(50, 188, 173, 0.2)',
                    }}
                  >
                    <img
                      src={pixIcon}
                      alt="Pix"
                      style={{
                        height: 22, width: 22,
                        background: 'white', padding: 3, borderRadius: 4,
                      }}
                    />
                    Assinar com Pix
                  </button>
                </div>
              )}

              {/* Card update happens automatically on click via handleCardClick */}
            </div>
          )}
        </ScrollableContent>
      </ModalContainer>
    );
  };

    const CheckoutContainer = () => {
    const [stripeError, setStripeError] = useState<string | null>(null);
    const [retryingStripe, setRetryingStripe] = useState(false);
    const stripe = useMemo(() => getStripe(), [checkoutKey]);

    useEffect(() => {
      let active = true;

      stripe
        .then(() => {
          if (!active) return;
          setStripeError(null);
          setRetryingStripe(false);
        })
        .catch((error) => {
          if (!active) return;
          const message = error instanceof Error ? error.message : "Não foi possível carregar o Stripe.";
          setStripeError(message);
          setRetryingStripe(false);
        });

      return () => {
        active = false;
      };
    }, [stripe]);

    useEffect(() => {
      if (completed) {
        toast.success("Pagamento realizado com sucesso!", { ...TOAST_CONFIG, toastId: 'payment-success' });
        closeModal();
        // Refresh plans to show updated subscription status
        refreshPlans();
      }
    }, [completed]); // Removed function dependencies to prevent infinite loop


    return (
      <ModalContainer step="checkout">
        <Header>
          <HeaderTitleWrapper>
            <StyledReturnButton onClick={() => setStep("plans")} />
            <h1 id="subscription-modal-title">Finalizar Pagamento</h1>
          </HeaderTitleWrapper>
          <HeaderActions>
            <CloseButton onClick={closeModal} aria-label="Fechar modal">
              <CloseIcon />
            </CloseButton>
          </HeaderActions>
        </Header>

        <ScrollableContent step="checkout">
          <CheckoutWrapper>
            {!clientSecret && !stripeError ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' }}>
                <Skeleton variant="rounded" width="100%" height={18} style={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" width="100%" height={44} style={{ borderRadius: 8 }} />
                <div style={{ display: 'flex', gap: 12 }}>
                  <Skeleton variant="rounded" width="50%" height={44} style={{ borderRadius: 8 }} />
                  <Skeleton variant="rounded" width="50%" height={44} style={{ borderRadius: 8 }} />
                </div>
                <Skeleton variant="rounded" width="40%" height={14} style={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" width="100%" height={44} style={{ borderRadius: 8 }} />
                <Skeleton variant="rounded" width="100%" height={48} style={{ borderRadius: 8, marginTop: 4 }} />
              </div>
            ) : stripeError ? (
              <StripeErrorCard>
                <p>Não foi possível carregar o Stripe. Verifique a conexão ou bloqueadores de conteúdo e tente novamente.</p>
                <CTA
                  variant="contained"
                  onClick={() => {
                    setRetryingStripe(true);
                    setStripeError(null);
                    setCheckoutKey((prev) => prev + 1);
                  }}
                  startIcon={retryingStripe ? <CircularProgress size={18} /> : undefined}
                  disabled={retryingStripe}
                >
                  {retryingStripe ? "Recarregando..." : "Tentar novamente"}
                </CTA>
              </StripeErrorCard>
            ) : (
              <Elements
                key={checkoutKey}
                stripe={stripe}
                options={{
                  clientSecret: clientSecret || '',
                  appearance: { theme: isDarkStripeTheme ? 'night' : 'stripe' },
                }}
              >
                <SubscriptionCheckoutForm
                  intentType={intentType || "payment"}
                  onComplete={async () => {
                    toast.success("Assinatura ativada com sucesso!", TOAST_CONFIG);
                    await refreshPlans();
                    closeModal();
                  }}
                  onError={(msg: string) => {
                    toast.error(msg || "Erro no pagamento", TOAST_CONFIG);
                  }}
                />
              </Elements>
            )}
          </CheckoutWrapper>
        </ScrollableContent>
      </ModalContainer>
    );
  };

  const CancelConfirmationModal = () => {
    if (!planToCancel) return null;
    
    const formattedExpiration = formatSubscriptionExpiration(planToCancel.subscription_expires_at);
    
    return (
      <ConfirmationOverlay onClick={() => setShowCancelConfirmation(false)}>
        <ConfirmationContainer onClick={(e) => e.stopPropagation()}>
          <ConfirmationContent>
            <h3>Cancelar assinatura?</h3>
            
            {formattedExpiration ? (
              <p>Após o cancelamento, você ainda terá acesso às funcionalidades de assinante premium até <strong>{formattedExpiration}</strong>.</p>
            ) : (
              <p>Você perderá o acesso aos recursos premium.</p>
            )}
          </ConfirmationContent>
          
          <ConfirmationActions>
            <ConfirmationButton
              variant="outlined"
              onClick={() => setShowCancelConfirmation(false)}
            >
              Manter plano
            </ConfirmationButton>
            <ConfirmationButton
              variant="contained"
              color="error"
              onClick={async () => {
                setShowCancelConfirmation(false);
                setPlanToCancel(null);
                await cancelSubscription(planToCancel.id);
              }}
              startIcon={cancelLoading === `cancel-${planToCancel.id}` ? <CircularProgress size={16} /> : undefined}
              disabled={cancelLoading === `cancel-${planToCancel.id}`}
            >
              {cancelLoading === `cancel-${planToCancel.id}` ? "Cancelando..." : "Cancelar o plano"}
            </ConfirmationButton>
          </ConfirmationActions>
        </ConfirmationContainer>
      </ConfirmationOverlay>
    );
  };

  const PixSubscriptionContainer = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [pixState, setPixState] = useState<"input" | "loading" | "pending" | "approved" | "rejected" | "error">("input");
    const [cpf, setCpf] = useState('');
    const [name, setName] = useState('');
    const [authData, setAuthData] = useState<any>(null);
    const [copied, setCopied] = useState(false);
    const [inputError, setInputError] = useState('');
    const pollRef = useRef<NodeJS.Timer | null>(null);

    const amount = 59.90;

    const formatCpf = (value: string) => {
      const d = value.replace(/\D/g, '').slice(0, 11);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
      if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
      return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    };

    const handleCreate = async () => {
      const cpfDigits = cpf.replace(/\D/g, '');
      if (cpfDigits.length !== 11) { setInputError('CPF inválido'); return; }
      if (!name.trim()) { setInputError('Informe seu nome completo'); return; }
      setInputError('');
      setPixState("loading");
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({
          amount: amount.toString(),
          purpose: 'subscription',
          payer_cpf: cpfDigits,
          payer_name: name.trim(),
          frequency: 'MENSAL',
        });
        const response = await fetch(`${API_BASE}/api/billing/pix/recurring/create-auth?${params}`, {
          method: 'POST',
          headers: { 'Authorization': `Auth ${token}` },
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Erro ao criar autorização');
        }
        const data = await response.json();
        setAuthData(data);
        setPixState("pending");
      } catch (error: any) {
        setPixState("error");
        toast.error(error.message || 'Erro ao criar autorização Pix', TOAST_CONFIG);
      }
    };

    // Poll for approval
    useEffect(() => {
      if (pixState !== "pending" || !authData) return;
      const poll = async () => {
        try {
          const token = await getAccessTokenSilently();
          const resp = await fetch(`${API_BASE}/api/billing/pix/recurring/auth-status/${authData.authorization_id}`, {
            headers: { 'Authorization': `Auth ${token}` },
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.status === 'APPROVED') {
              try {
                fireGoogleAdsConversion({
                  transaction_id: authData?.authorization_id || ('pix_' + Date.now().toString()),
                  value: 59.90,
                  currency: 'BRL',
                });
              } catch { /* non-fatal */ }
              setPixApproved(true);
              setPixState("approved");
              refreshPlans();
              onSubscriptionChange?.();
            } else if (data.status === 'REJECTED') {
              setPixState("rejected");
            }
          }
        } catch {}
      };
      poll();
      pollRef.current = setInterval(poll, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [pixState, authData]);


    const handleCopy = async () => {
      if (authData?.emv) {
        await navigator.clipboard.writeText(authData.emv);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    return (
      <ModalContainer step="payment-methods">
        <Header>
          <HeaderTitleWrapper>
            <StyledReturnButton onClick={() => setStep("payment-methods")} />
            <h1>Assinar com Pix</h1>
          </HeaderTitleWrapper>
          <CloseButton onClick={closeModal} aria-label="Fechar modal"><CloseIcon /></CloseButton>
        </Header>

        <ScrollableContent step="payment-methods">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', maxWidth: 440 }}>
            {pixApproved ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CheckIcon style={{ fontSize: 64, color: '#10b981' }} />
                <h3 style={{ color: '#10b981', fontWeight: 700, fontSize: '1.3rem', margin: '12px 0 8px' }}>Assinatura ativada!</h3>
                <p style={{ color: theme?.payment_modal_text_secondary, fontSize: '0.95rem' }}>
                  Sua assinatura de R$ {amount.toFixed(2).replace('.', ',')}/mês via Pix Automático está ativa.
                </p>
              </div>
            ) : pixState === "rejected" ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <h3 style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.2rem', margin: '0 0 12px' }}>Autorização rejeitada</h3>
                <p style={{ color: theme?.payment_modal_text_secondary, fontSize: '0.95rem', marginBottom: 16 }}>
                  O Pix Automático foi rejeitado pelo seu banco.
                </p>
                <button onClick={() => { setPixState("input"); setAuthData(null); }}
                  style={{ padding: '12px 24px', borderRadius: 8, background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                  Tentar novamente
                </button>
              </div>
            ) : pixState === "pending" && authData ? (
              <>
                <div style={{
                  textAlign: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: '1.2rem',
                  color: theme?.payment_modal_text,
                }}>
                  R$ {amount.toFixed(2).replace('.', ',')}/mês
                </div>
                {authData.emv && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <p style={{
                      fontSize: '0.85rem', color: theme?.payment_modal_text_secondary,
                      lineHeight: 1.5, margin: 0, textAlign: 'center',
                    }}>
                      Abra o app do seu banco, escaneie o QR Code abaixo ou copie e cole o código Pix para autorizar a assinatura.
                    </p>
                    <div style={{ background: '#fff', border: '2px solid #e2e8f0', borderRadius: 16, padding: 16 }}>
                      <QRCodeSVG value={authData.emv} size={200} level="M" includeMargin={true} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                      <input readOnly value={authData.emv} onClick={handleCopy}
                        style={{
                          flex: 1, padding: '10px 12px', fontSize: '0.75rem',
                          fontFamily: "'Monaco', 'Menlo', monospace",
                          border: '2px solid #e2e8f0', borderRadius: 8, background: '#f8fafc',
                          color: '#475569', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', outline: 'none',
                        }} />
                      <button onClick={handleCopy}
                        style={{
                          padding: '10px 14px', background: '#f1f5f9', border: '2px solid #e2e8f0',
                          borderRadius: 8, cursor: 'pointer', color: copied ? '#10b981' : '#64748b',
                        }}>
                        {copied ? '✓' : '📋'}
                      </button>
                    </div>
                    {copied && <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 500 }}>Copiado!</span>}
                  </div>
                )}
                {authData.deep_link && (
                  <a href={authData.deep_link} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '12px 24px', borderRadius: 8, background: '#3b82f6', color: '#fff',
                      textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem',
                    }}>
                    Abrir no app do banco
                  </a>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                }}>
                  <CircularProgress size={16} style={{ color: '#f59e0b' }} />
                  <span style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 500 }}>Aguardando aprovação no app do banco...</span>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.9rem', color: theme?.payment_modal_text_secondary, lineHeight: 1.5, margin: 0 }}>
                  Autorize o Pix Automático no app do seu banco para pagar sua assinatura de R$ {amount.toFixed(2).replace('.', ',')}/mês automaticamente.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: theme?.payment_modal_text }}>CPF do pagador</label>
                    <input type="text" placeholder="000.000.000-00" value={cpf}
                      onChange={(e) => setCpf(formatCpf(e.target.value))}
                      disabled={pixState === "loading"}
                      style={{
                        padding: '12px 14px', fontSize: '1rem', border: `2px solid ${theme?.payment_modal_border || '#e2e8f0'}`,
                        borderRadius: 10, outline: 'none', background: theme?.payment_modal_bg || '#fff',
                        color: theme?.payment_modal_text,
                      }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: theme?.payment_modal_text }}>Nome completo</label>
                    <input type="text" placeholder="Seu nome completo" value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={pixState === "loading"}
                      style={{
                        padding: '12px 14px', fontSize: '1rem', border: `2px solid ${theme?.payment_modal_border || '#e2e8f0'}`,
                        borderRadius: 10, outline: 'none', background: theme?.payment_modal_bg || '#fff',
                        color: theme?.payment_modal_text,
                      }} />
                  </div>
                  {inputError && <span style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 500 }}>{inputError}</span>}
                </div>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 10, padding: 16,
                }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0d9488' }}>Como funciona?</span>
                  <span style={{ fontSize: '0.85rem', color: '#475569' }}>1. Você autoriza o Pix Automático no app do seu banco</span>
                  <span style={{ fontSize: '0.85rem', color: '#475569' }}>2. A cobrança é feita automaticamente a cada mês</span>
                  <span style={{ fontSize: '0.85rem', color: '#475569' }}>3. Você pode cancelar a qualquer momento</span>
                </div>
                <button onClick={handleCreate}
                  disabled={pixState === "loading" || !cpf || !name}
                  style={{
                    padding: '14px 24px', borderRadius: 10,
                    background: pixState === "loading" || !cpf || !name ? '#94a3b8' : 'linear-gradient(135deg, #32BCAD 0%, #2A9D8F 100%)',
                    color: '#fff', border: 'none', fontSize: '1rem', fontWeight: 600,
                    cursor: pixState === "loading" || !cpf || !name ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: pixState === "loading" || !cpf || !name ? 0.6 : 1,
                  }}>
                  {pixState === "loading" ? <><CircularProgress size={18} color="inherit" /> Criando autorização...</> : "Criar autorização Pix"}
                </button>
              </>
            )}
          </div>
        </ScrollableContent>
      </ModalContainer>
    );
  };

  return (
    <StyledModal
      open={openModal}
      onClose={closeModal}
      BackdropProps={{ onClick: (e: React.MouseEvent) => e.stopPropagation() }}
      aria-labelledby="subscription-modal-title"
    >
      <>
        {step === "plans" ? <PlansContainer /> : step === "invoice-config" ? <InvoiceConfigContainer /> : step === "payment-methods" ? <PaymentMethodsContainer /> : step === "pix-subscription" ? <PixSubscriptionContainer /> : <CheckoutContainer />}
        {showCancelConfirmation && <CancelConfirmationModal />}
      </>
    </StyledModal>
  );
}

function AddCardForm({ onComplete, onError }: { onComplete: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const result = await stripe.confirmSetup({ elements, redirect: "if_required" });
      if (result.error) {
        onError(result.error.message || "Erro ao salvar cartão");
      } else {
        onComplete();
      }
    } catch (err: any) {
      onError(err.message || "Erro inesperado");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PaymentElement loader="always" />
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          padding: '12px 24px', borderRadius: 8, border: 'none', fontSize: '0.95rem', fontWeight: 600,
          background: !stripe || processing ? '#ccc' : '#4f46e5', color: '#fff',
          cursor: !stripe || processing ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {processing ? <><CircularProgress size={18} color="inherit" /> Salvando...</> : "Salvar cartão"}
      </button>
    </form>
  );
}

function SubscriptionCheckoutForm({ intentType, onComplete, onError }: { intentType: string; onComplete: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      let result;
      if (intentType === "setup") {
        result = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
        });
      } else {
        result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });
      }

      if (result.error) {
        onError(result.error.message || "Erro no pagamento");
      } else {
        // Server-side upload via Stripe webhook is the source of truth; this gtag
        // fire is a client-side complement. Google dedups same gclid + Count:One.
        try {
          const txId = (result as any)?.paymentIntent?.id
            || (result as any)?.setupIntent?.id
            || ('client_' + Date.now().toString());
          fireGoogleAdsConversion({
            transaction_id: txId,
            value: 59.90,
            currency: 'BRL',
          });
        } catch { /* non-fatal */ }
        onComplete();
      }
    } catch (err: any) {
      onError(err.message || "Erro inesperado");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
      <PaymentElement loader="always" />
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          background: '#4f46e5',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: !stripe || processing ? 'not-allowed' : 'pointer',
          opacity: !stripe || processing ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '44px',
        }}
      >
        {processing ? "Processando..." : "Assinar"}
      </button>
    </form>
  );
}

const HeaderTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledReturnButton = styled(KeyboardReturnIcon)`
  cursor: pointer;
  color: ${props => props.theme.payment_modal_text_secondary};
  font-size: 1.2rem;
  &:hover { color: ${props => props.theme.payment_modal_text}; }
`;

const CloseButton = styled("button")`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.payment_modal_text_secondary};
  padding: 4px;
  display: flex;
  border-radius: 4px;
  &:hover { background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f4f5f7'}; }
`;

const StyledModal = styledMUI(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div<{ step?: string }>`
  background: ${props => props.theme.payment_modal_background};
  color: ${props => props.theme.payment_modal_text};
  border: 1px solid ${props => props.theme.payment_modal_border};
  width: ${props =>
    props.step === 'invoice-config' ? 'min(95vw, 560px)' :
    props.step === 'checkout' ? 'min(95vw, 480px)' :
    props.step === 'payment-methods' ? 'min(95vw, 560px)' :
    'min(95vw, 860px)'};
  max-width: 100%;
  height: ${props => props.step === 'checkout' ? '85vh' : 'auto'};
  max-height: 90vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  outline: none;
  overflow: hidden;
  @media (max-width: 768px) {
    width: 98vw;
    border-radius: 10px;
  }
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.payment_modal_border};
  flex-shrink: 0;
  h1 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: ${props => props.theme.payment_modal_text};
    letter-spacing: -0.01em;
  }
  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const ScrollableContent = styled.div<{ step?: string }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${props => props.step === 'checkout' ? '20px 24px 32px' : '24px'};
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  align-items: ${props => props.step === 'checkout' || props.step === 'payment-methods' ? 'center' : 'stretch'};
  @media (max-width: 768px) {
    padding: 20px;
  }
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.payment_modal_scrollbar};
    border-radius: 3px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshIndicator = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.payment_modal_text_secondary};
  opacity: 0.5;
`;

const CardsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

const PlanCard = styled.div`
  flex: 1 1 280px;
  max-width: 400px;
  background: ${props => props.theme.payment_modal_background};
  border: 1px solid ${props => props.theme.payment_modal_border};
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: border-color 0.15s;
  &:hover { border-color: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#c4c9d4'}; }
`;

const CheckoutWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  max-width: 480px;
  min-height: 0;
  justify-content: center;
  align-items: flex-start;
`;

const StripeErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${props => props.theme.payment_modal_border};
  background: ${props => props.theme.payment_modal_background};
  color: ${props => props.theme.payment_modal_text};
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  font-size: 0.9rem;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PlanName = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.payment_modal_text};
  letter-spacing: -0.01em;
`;

const Price = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.payment_modal_text};
  letter-spacing: -0.02em;
  span {
    font-size: 2rem;
    margin-left: 1px;
  }
`;

const Unit = styled.span`
  font-size: 0.85rem;
  font-weight: 400;
  color: ${props => props.theme.payment_modal_text_secondary};
`;

const Tagline = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.payment_modal_text_secondary};
  margin: 0;
  line-height: 1.4;
`;

const CancellationInfo = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.plan_warning_color};
  line-height: 1.4;
`;

const FeatureList = styled.ul<{ planId?: string }>`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  li {
    font-size: 0.85rem;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    color: ${props => props.theme.payment_modal_text_secondary};
    line-height: 1.4;
    ${props => props.planId === "free" ? `
      display: list-item;
      list-style-type: disc;
      list-style-position: inside;
      margin-left: 4px;
    ` : ''}
    svg {
      margin-top: 2px;
      font-size: 0.9rem;
      color: ${props => props.theme.plan_success_color};
      flex-shrink: 0;
    }
  }
`;

const CTA = styled(Button)<{ component?: string }>`
  && {
    text-transform: none;
    font-weight: 600;
    font-size: 0.875rem;
    padding: 10px 0;
    border-radius: 6px;
    background-color: ${(props: any) => props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5'};
    color: ${(props: any) => props.theme.mode === 'dark' ? '#0b1120' : '#fff'};
    border: none;
    letter-spacing: -0.01em;
    &:hover {
      background-color: ${(props: any) => props.theme.mode === 'dark' ? props.theme.blue_color_hover : '#4338ca'};
      color: ${(props: any) => props.theme.mode === 'dark' ? '#0b1120' : '#fff'};
    }
    &.MuiButton-outlined {
      background-color: transparent;
      color: ${(props: any) => props.theme.payment_modal_text};
      border: 1px solid ${(props: any) => props.theme.payment_modal_border};
      &:hover {
        background: ${(props: any) => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f6f8fa'};
        border-color: ${(props: any) => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#c4c9d4'};
      }
    }
  }
`;

const SecondaryCTA = styled(Button)`
  && {
    text-transform: none;
    font-weight: 500;
    font-size: 0.8rem;
    padding: 8px 0;
    border-radius: 6px;
    color: ${props => props.theme.payment_modal_text_secondary};
    border: 1px solid ${props => props.theme.payment_modal_border};
    &:hover {
      background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f6f8fa'};
    }
  }
`;

const HelpLink = styled.a`
  font-size: 0.7rem;
  color: ${props => props.theme.payment_modal_text_secondary};
  margin-top: auto;
  text-align: center;
  text-decoration: none;
  opacity: 0.7;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const FiscalDataChoice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
  padding: 32px 16px;
  h2 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: ${props => props.theme.payment_modal_text};
    letter-spacing: -0.01em;
  }
  p {
    font-size: 0.875rem;
    color: ${props => props.theme.payment_modal_text_secondary};
    margin: 0;
    max-width: 420px;
    line-height: 1.5;
  }
`;

const ChoiceButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 420px;
  @media (max-width: 768px) { flex-direction: column; }
`;

const CancelledBadge = styled.span`
  color: ${props => props.theme.plan_warning_color};
  font-size: 0.65rem;
  font-weight: 500;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255, 153, 102, 0.15)' : 'rgba(255, 127, 64, 0.1)'};
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const CurrentPlanBadge = styled.span`
  color: ${props => props.theme.plan_success_color};
  font-size: 0.65rem;
  font-weight: 500;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(136, 221, 102, 0.15)' : 'rgba(121, 212, 75, 0.1)'};
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const PlanNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CancelButton = styled(Button)`
  && {
    text-transform: none;
    font-weight: 500;
    font-size: 0.7rem;
    padding: 3px 8px;
    min-width: auto;
    border-radius: 4px;
    margin-left: auto;
    border: none;
    color: ${props => props.theme.plan_warning_color};
    &:hover { background: rgba(255, 127, 64, 0.1); border: none; }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const PlatformFormDesc = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.payment_modal_text_secondary};
  margin: 0;
  line-height: 1.5;
`;

const InvoiceTypeContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const InvoiceTypeOption = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${(props) => props.selected
    ? (props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5')
    : props.theme.payment_modal_border};
  background: ${(props) => props.selected
    ? (props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5')
    : 'transparent'};
  color: ${(props) => props.selected
    ? (props.theme.mode === 'dark' ? '#0b1120' : '#fff')
    : props.theme.payment_modal_text_secondary};
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 6px;
  transition: all 0.15s;
  font-size: 0.85rem;
  svg { font-size: 1rem; }
  &:hover {
    border-color: ${(props) => props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5'};
  }
`;

const InvoiceTypeText = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
`;

const PlatformFormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 520px;
`;

const PlatformFormRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PlatformFormRowSelect = styled(PlatformFormRow)``;

const PlatformFormLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.theme.payment_modal_text_secondary};
  margin: 8px 0 3px 0;
`;

const PlatformFormInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  font-size: 0.875rem;
  border: 1px solid ${props => props.theme.payment_modal_border};
  border-radius: 6px;
  background: ${props => props.theme.payment_modal_background};
  color: ${props => props.theme.payment_modal_text};
  transition: border-color 0.15s;
  &:focus {
    border-color: ${props => props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5'};
    outline: none;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PlatformFormSelect = styled.select`
  width: 100%;
  height: 38px;
  padding-left: 12px;
  font-size: 0.875rem;
  border: 1px solid ${props => props.theme.payment_modal_border};
  border-radius: 6px;
  background: ${props => props.theme.payment_modal_background};
  color: ${props => props.theme.payment_modal_text};
  transition: border-color 0.15s;
  &:focus {
    border-color: ${props => props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5'};
    outline: none;
  }
  &:disabled {
    background: ${props => props.theme.payment_modal_input_disabled_bg};
  }
`;

const PlatformButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 12px 0 0 0;
`;

const PlatformSaveButton = styled.button`
  font-size: 0.875rem;
  font-weight: 600;
  padding: 10px 20px;
  min-width: 180px;
  background: ${props => props.theme.mode === 'dark' ? props.theme.blue_color : '#4f46e5'};
  color: ${props => props.theme.mode === 'dark' ? '#0b1120' : '#fff'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: ${props => props.theme.mode === 'dark' ? props.theme.blue_color_hover : '#4338ca'};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.4)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
`;

const ConfirmationContainer = styled.div`
  background: ${props => props.theme.payment_modal_background};
  border-radius: 10px;
  padding: 24px;
  width: min(90vw, 440px);
  border: 1px solid ${props => props.theme.payment_modal_border};
`;

const ConfirmationContent = styled.div`
  margin-bottom: 20px;
  color: ${props => props.theme.payment_modal_text};
  h3 {
    margin: 0 0 8px 0;
    font-size: 1rem;
    font-weight: 600;
  }
  p {
    margin: 0;
    line-height: 1.5;
    font-size: 0.875rem;
    color: ${props => props.theme.payment_modal_text_secondary};
  }
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  @media (max-width: 480px) { flex-direction: column; }
`;

const ConfirmationButton = styled(Button)`
  && {
    text-transform: none;
    font-weight: 500;
    font-size: 0.85rem;
    padding: 8px 16px;
    border-radius: 6px;
    &.MuiButton-outlined {
      color: ${props => props.theme.mode === 'dark' ? '#e5e7eb' : '#374151'} !important;
      border-color: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : '#d1d5db'};
      &:hover {
        background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f3f4f6'};
      }
    }
    &.MuiButton-containedError {
      background: #ef4444;
      color: #fff !important;
      &:hover { background: #dc2626; }
    }
  }
`;

